import clsx from "clsx";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import type { TypoStyle } from "../Typo";

import { FieldError } from "../FieldError";
import { BaseArrowDownIcon, CheckIcon } from "../Icons";
import { SkeletonLoader } from "../SkeletonLoader";
import { Typo } from "../Typo";

export type SelectV3Option<T extends string | number = string> = {
  label: string;
  value: T;
  description?: string;
};

export type SelectV3RenderParams<T extends string | number = string> = {
  option: SelectV3Option<T>;
  isSelected: boolean;
};

export type SelectV3Props<T extends string | number = string> = {
  id?: string;
  value?: SelectV3Option<T> | null;
  options: SelectV3Option<T>[];
  isMobile?: boolean;
  label?: string;
  className?: string;
  placeholder?: string;
  subLabel?: string;
  placeholderErrorText?: string;
  error?: string;
  enableSearch?: boolean;
  modal?: boolean;
  disabled?: boolean;
  matchTriggerWidth?: boolean;
  onChange: (value: SelectV3Option<T>) => void;
  renderOptionCell?: (params: SelectV3RenderParams<T>) => React.ReactNode;
  renderValue?: (value: SelectV3Option<T>) => React.ReactNode;
  labelType?: TypoStyle;
  smLabelType?: TypoStyle;
  dropdownListClassName?: string;
  /** Renders skeleton rows in place of options while `true`. Default `false`. */
  isLoading?: boolean;
  /**
   * Replaces the default "no results" row when zero options match, receiving
   * the live search query. Rendered outside the option list, so its content
   * never triggers `handleOptionClick`'s `setOpen(false)` and stays visible
   * for the click-outside handler — callers can render actionable content
   * (e.g. a "create" row) here without the dropdown auto-closing.
   */
  renderEmptyState?: (query: string) => React.ReactNode;
};

const DefaultOptionCell = <T extends string | number>({
  option,
  isSelected,
}: SelectV3RenderParams<T>) => (
  <>
    <div className="flex flex-col gap-1">
      <Typo
        element="p"
        type="bodySM"
        textColor={isSelected ? "text-text-selected" : undefined}
      >
        {option.label}
      </Typo>
      {option.description && (
        <Typo
          element="p"
          type="bodyXS"
          textColor={isSelected ? "text-text-selected" : "text-text-secondary"}
        >
          {option.description}
        </Typo>
      )}
    </div>
    {isSelected && (
      <CheckIcon className="h-4 w-4 [&_path]:stroke-stroke-selected" />
    )}
  </>
);

export const SelectV3 = <T extends string | number = string>({
  id,
  value,
  options,
  isMobile,
  label,
  className,
  subLabel,
  placeholder,
  placeholderErrorText,
  error,
  enableSearch = true,
  modal = false,
  disabled = false,
  matchTriggerWidth = true,
  onChange,
  renderOptionCell,
  renderValue,
  labelType = "labelMD",
  smLabelType = "labelSM",
  dropdownListClassName: customDropdownListClassName,
  isLoading = false,
  renderEmptyState,
}: SelectV3Props<T>) => {
  const { t } = useTranslation();

  const preSelected = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputContainerRef = useRef<HTMLDivElement | null>(null);
  const dropdownContainerRef = useRef<HTMLDivElement | null>(null);
  const dropdownListRef = useRef<HTMLDivElement | null>(null);
  const selectedOptionRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value?.label || "");
  const [searchFilter, setSearchFilter] = useState("");
  // Start with position off-screen to prevent flash at (0,0) before position is calculated
  const [dropdownPosition, setDropdownPosition] = useState({
    top: -9999,
    left: -9999,
    width: 0,
    maxHeight: 384,
  });
  // Track when position has been calculated to avoid flash at wrong position on first render
  const [positionReady, setPositionReady] = useState(false);
  const HEADER_OFFSET = 80; // Offset for fixed header height
  const VIEWPORT_MARGIN = 8;
  const DROPDOWN_GAP = 8;
  const DEFAULT_DROPDOWN_MAX_HEIGHT = 384;

  const placeholderError =
    placeholderErrorText ?? t("global_invalidSelectOptionsError");

  const openDropdown = () => {
    scrollToTop();
    setSearchFilter("");
    setOpen(true);
  };

  const handleInputFocus = () => {
    if (disabled) return;
    // Search-enabled selects open on focus so the user can start typing right
    // away. Non-search (readOnly input / button) selects open and close via the
    // click toggle below instead, so clicking the trigger again collapses the
    // dropdown rather than re-opening it.
    if (!enableSearch) return;
    openDropdown();
  };

  // Toggle handler for non-search triggers: a second click on the trigger
  // closes the open dropdown (previously it stayed open, so the only way to
  // dismiss it was clicking outside or selecting an option).
  const handleTriggerClick = () => {
    if (disabled || enableSearch) return;
    if (open) {
      setSearchTerm(preSelected.current || "");
      setOpen(false);
    } else {
      openDropdown();
    }
  };

  const scrollToTop = () => {
    if (isMobile && containerRef.current && enableSearch) {
      const element = containerRef.current;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - HEADER_OFFSET;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (enableSearch) {
      setSearchTerm(e.target.value);
      setSearchFilter(e.target.value);
    }
  };

  const handleOptionClick = (option: SelectV3Option<T>) => {
    setOpen(false);
    setSearchTerm(option.label);
    preSelected.current = option.label;
    onChange(option);
  };

  useEffect(() => {
    preSelected.current = value?.label ?? null;
    setSearchTerm(value?.label || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideContainer =
        containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown =
        !dropdownContainerRef.current ||
        !dropdownContainerRef.current.contains(target);

      if (isOutsideContainer && isOutsideDropdown) {
        setSearchTerm(preSelected.current || "");
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (open && selectedOptionRef.current && dropdownListRef.current) {
      const container = dropdownListRef.current;
      const element = selectedOptionRef.current;

      const scrollTop =
        element.offsetTop -
        container.offsetTop -
        container.clientHeight / 2 +
        element.clientHeight / 2;

      container.scrollTop = scrollTop;
    }
  }, [open]);

  // Reset position state when dropdown closes to prevent flash at old position on reopen
  useEffect(() => {
    if (!open) {
      setPositionReady(false);
      setDropdownPosition((prev) => ({ ...prev, top: -9999, left: -9999 }));
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!modal || !open || !inputContainerRef.current) return;

    const updatePosition = () => {
      const triggerRect = inputContainerRef.current?.getBoundingClientRect();
      if (!triggerRect) return;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const measuredHeight = dropdownListRef.current?.scrollHeight ?? 0;
      const dropdownHeight = Math.min(
        DEFAULT_DROPDOWN_MAX_HEIGHT,
        measuredHeight || DEFAULT_DROPDOWN_MAX_HEIGHT,
      );

      const spaceBelow =
        viewportHeight - triggerRect.bottom - DROPDOWN_GAP - VIEWPORT_MARGIN;
      const spaceAbove = triggerRect.top - DROPDOWN_GAP - VIEWPORT_MARGIN;
      const shouldDropUp =
        spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      const width = matchTriggerWidth
        ? triggerRect.width
        : (dropdownContainerRef.current?.getBoundingClientRect().width ??
          triggerRect.width);
      const maxLeft = viewportWidth - VIEWPORT_MARGIN - width;
      const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(triggerRect.left, maxLeft),
      );

      const availableHeight = shouldDropUp ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(
        80,
        Math.min(DEFAULT_DROPDOWN_MAX_HEIGHT, availableHeight),
      );
      const renderedHeight = Math.min(dropdownHeight, maxHeight);

      const preferredTop = shouldDropUp
        ? triggerRect.top - DROPDOWN_GAP - renderedHeight
        : triggerRect.bottom + DROPDOWN_GAP;
      const maxTop = viewportHeight - VIEWPORT_MARGIN - maxHeight;
      const top = Math.max(VIEWPORT_MARGIN, Math.min(preferredTop, maxTop));

      setDropdownPosition({
        top,
        left,
        width,
        maxHeight,
      });
    };

    // Find all scrollable parents to attach scroll listeners
    const getScrollableParents = (element: HTMLElement): HTMLElement[] => {
      const scrollableParents: HTMLElement[] = [];
      let parent = element.parentElement;
      while (parent) {
        const style = getComputedStyle(parent);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        if (
          overflowY === "auto" ||
          overflowY === "scroll" ||
          overflowX === "auto" ||
          overflowX === "scroll"
        ) {
          scrollableParents.push(parent);
        }
        parent = parent.parentElement;
      }
      return scrollableParents;
    };

    const scrollableParents = getScrollableParents(inputContainerRef.current);

    // Close dropdown when scrolling inside a scrollable parent (e.g. modal)
    // to prevent the dropdown from floating outside modal boundaries
    const closeOnScroll = () => setOpen(false);

    // Calculate initial position immediately (before paint)
    const triggerRect = inputContainerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const estimatedDropdownHeight = DEFAULT_DROPDOWN_MAX_HEIGHT;
    const spaceBelow =
      viewportHeight - triggerRect.bottom - DROPDOWN_GAP - VIEWPORT_MARGIN;
    const shouldDropUp =
      spaceBelow < estimatedDropdownHeight && triggerRect.top > spaceBelow;

    setDropdownPosition({
      top: shouldDropUp
        ? triggerRect.top - DROPDOWN_GAP - estimatedDropdownHeight
        : triggerRect.bottom + DROPDOWN_GAP,
      left: triggerRect.left,
      width: triggerRect.width,
      maxHeight: estimatedDropdownHeight,
    });
    setPositionReady(false);

    // Use requestAnimationFrame to recalculate with actual measurements
    const rafId = requestAnimationFrame(() => {
      updatePosition();
      // Recalculate after dropdown has rendered to get accurate height
      requestAnimationFrame(() => {
        updatePosition();
        setPositionReady(true);
      });
    });

    // Close dropdown when scrolling inside parent containers
    for (const parent of scrollableParents) {
      parent.addEventListener("scroll", closeOnScroll);
    }
    window.addEventListener("resize", updatePosition);

    return () => {
      cancelAnimationFrame(rafId);
      for (const parent of scrollableParents) {
        parent.removeEventListener("scroll", closeOnScroll);
      }
      window.removeEventListener("resize", updatePosition);
    };
  }, [modal, open, matchTriggerWidth]);

  const optionsFiltered = enableSearch
    ? options.filter((option) =>
        option.label.toLowerCase().includes((searchFilter || "").toLowerCase()),
      )
    : options;

  const inputClassName = clsx(
    "h-10 w-full rounded-md p-2 pr-8 font-body text-[0.875rem] font-normal focus:outline-none",
    "placeholder-text-tertiary placeholder:font-body placeholder:text-sm placeholder:font-normal placeholder:leading-normal",
    error && "ring-2 ring-stroke-error",
    !enableSearch && !disabled && "cursor-pointer",
    disabled
      ? "cursor-not-allowed bg-surface-disabled text-text-disabled"
      : clsx(
          "bg-surface-tonal-default text-text-primary",
          // Only apply the hover/focus ring when there's no error, so focusing
          // an invalid field keeps the error ring above instead of hiding it.
          !error &&
            "hover:ring-2 hover:ring-stroke-focused focus:ring-2 focus:ring-stroke-focused",
        ),
  );

  // Padding/max-height applies whenever the list renders scrollable content:
  // matched options (unchanged), loading skeletons, or a caller-provided
  // empty state. The built-in "no results" row keeps its own p-4 wrapper
  // below instead, so this stays false for that default case exactly as
  // before — no double padding for existing consumers.
  const hasDropdownContent =
    isLoading ||
    optionsFiltered.length > 0 ||
    (optionsFiltered.length === 0 && !!renderEmptyState);

  const dropdownListClassName = clsx(
    hasDropdownContent && "max-h-[384px] overflow-y-auto rounded-lg",
    customDropdownListClassName ?? (hasDropdownContent && "p-4"),
    "[&::-webkit-scrollbar]:w-1",
    "[&::-webkit-scrollbar-track]:rounded-full",
    "[&::-webkit-scrollbar-thumb]:min-h-20",
    "[&::-webkit-scrollbar-thumb]:bg-surface-tonal-bold",
    "[&::-webkit-scrollbar-thumb]:rounded-full",
  );

  const renderOption =
    renderOptionCell ??
    ((params: SelectV3RenderParams<T>) => <DefaultOptionCell {...params} />);

  const dropdown = (
    <div
      ref={dropdownContainerRef}
      className={clsx(
        "border-border-stroke-primary rounded-lg border bg-surface-primary shadow-dropdown",
        modal
          ? "fixed z-floating"
          : clsx("absolute z-50 mt-2", matchTriggerWidth ? "w-full" : "w-auto"),
      )}
      style={
        modal
          ? {
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              ...(matchTriggerWidth ? { width: dropdownPosition.width } : {}),
              // Use visibility:hidden to completely hide until position is ready
              visibility: positionReady ? "visible" : "hidden",
            }
          : undefined
      }
    >
      <div
        ref={dropdownListRef}
        className={dropdownListClassName}
        style={
          modal
            ? {
                maxHeight: dropdownPosition.maxHeight,
              }
            : undefined
        }
      >
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map((row) => (
              <SkeletonLoader key={row} motionReduce height={40} radius="md" />
            ))}
          </div>
        ) : optionsFiltered.length > 0 ? (
          optionsFiltered.map((option) => {
            const isSelected = value?.value === option.value;
            return (
              <div
                key={option.value}
                ref={isSelected ? selectedOptionRef : null}
                role="option"
                aria-selected={isSelected}
                className={clsx(
                  "flex min-w-[200px] cursor-pointer items-center justify-between rounded-md p-2 hover:bg-surface-tonal-default",
                  isSelected && "bg-state-selected",
                )}
                onClick={() => handleOptionClick(option)}
              >
                {renderOption({ option, isSelected })}
              </div>
            );
          })
        ) : renderEmptyState ? (
          renderEmptyState(searchFilter)
        ) : (
          <div className="p-4">
            <Typo element="p" type="bodySM" textColor="text-text-secondary">
              {t("global_no_results_found")}
            </Typo>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={clsx("flex w-full flex-col gap-y-2", className)}
      ref={containerRef}
    >
      {label && (
        <label htmlFor={id} className="flex flex-col gap-1">
          <Typo element="p" type={labelType} smType={smLabelType}>
            {label}
          </Typo>

          {subLabel && (
            <Typo element="p" type="bodyXS" textColor="text-text-secondary">
              {subLabel}
            </Typo>
          )}
        </label>
      )}
      <div className="relative" ref={inputContainerRef}>
        {!enableSearch && renderValue && value ? (
          <button
            id={id}
            type="button"
            onClick={handleTriggerClick}
            className={clsx(
              inputClassName,
              "flex h-auto min-h-10 flex-col justify-center py-2 text-left",
            )}
            disabled={disabled}
            aria-expanded={open}
            aria-haspopup="listbox"
          >
            {renderValue(value)}
          </button>
        ) : (
          <input
            id={id}
            value={searchTerm}
            className={inputClassName}
            onFocus={handleInputFocus}
            onClick={handleTriggerClick}
            onChange={handleInputChange}
            placeholder={error ? placeholderError : placeholder}
            readOnly={!enableSearch}
            disabled={disabled}
          />
        )}
        <div className="pointer-events-none absolute inset-y-0 end-3 flex items-center">
          <BaseArrowDownIcon
            className={clsx(
              "h-4 w-4 text-icon-secondary transition-transform",
              open && "rotate-180",
            )}
          />
        </div>
        {open && !modal && dropdown}
      </div>
      {open && modal && createPortal(dropdown, document.body)}
      {error && <FieldError text={error} className="text-text-error" />}
    </div>
  );
};
